import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { csrfToken } from '@/utils/csrf'

export default class extends Controller {
  static targets = ['input', 'progress', 'progressBar', 'progressText', 'fileList', 'dropZone', 'uploadArea']
  static values = {
    url: String,
    acceptTypes: { type: String, default: '.pdf,.doc,.docx,.odt' },
    maxSize: { type: Number, default: 10485760 } // 10MB
  }

  // Drag highlight and chip styling come from the markup so both brands can drive
  // this file. A class named only in here is outside every Tailwind content glob,
  // so Workays' bundle would never compile the endeavour defaults.
  static classes = ['dragActive', 'chip', 'chipLabel', 'chipRemove']

  static DEFAULT_CLASSES = {
    dragActive: ['border-endeavour-500', 'bg-endeavour-50'],
    chip: ['inline-flex', 'items-center', 'gap-1.5', 'px-3', 'py-1.5', 'bg-gray-100', 'rounded-full', 'text-sm', 'text-gray-700'],
    chipLabel: ['truncate', 'max-w-[160px]'],
    chipRemove: ['p-0.5', 'rounded-full', 'text-gray-400', 'hover:text-red-500', 'hover:bg-red-50', 'transition-colors']
  }

  classesFor(name) {
    const capitalised = name.charAt(0).toUpperCase() + name.slice(1)
    if (this[`has${capitalised}Class`]) return this[`${name}Classes`]

    return this.constructor.DEFAULT_CLASSES[name]
  }

  connect() {
    this.setupDropZone()
  }

  setupDropZone() {
    if (!this.hasDropZoneTarget) return

    const dropZone = this.dropZoneTarget

    const dragActive = this.classesFor('dragActive')

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropZone.classList.add(...dragActive)
    })

    dropZone.addEventListener('dragleave', (e) => {
      e.preventDefault()
      dropZone.classList.remove(...dragActive)
    })

    dropZone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropZone.classList.remove(...dragActive)
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.uploadFile(files[0])
      }
    })
  }

  triggerFileInput() {
    this.inputTarget.click()
  }

  handleFileSelected(event) {
    const file = event.target.files[0]
    if (!file) return
    this.uploadFile(file)
    // Reset file input so the same file can be re-selected
    event.target.value = ''
  }

  uploadFile(file) {
    // Client-side validation
    if (file.size > this.maxSizeValue) {
      this.showError(`File is too large. Maximum size is ${Math.round(this.maxSizeValue / 1048576)}MB.`)
      return
    }

    const acceptedExtensions = this.acceptTypesValue.split(',').map(ext => ext.trim().toLowerCase())
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
    if (!acceptedExtensions.includes(fileExtension)) {
      this.showError(`File type not accepted. Please upload: ${this.acceptTypesValue}`)
      return
    }

    this.showProgress()

    const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', this)
    upload.create((error, blob) => {
      if (error) {
        this.hideProgress()
        this.showError('Upload failed. Please try again.')
      } else {
        this.submitBlob(blob)
      }
    })
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', (event) => {
      const progress = Math.round((event.loaded / event.total) * 100)
      this.updateProgress(progress)
    })
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.add('pointer-events-none', 'opacity-50')
    }
    this.updateProgress(0)
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.remove('pointer-events-none', 'opacity-50')
    }
  }

  updateProgress(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
  }

  submitBlob(blob) {
    this.updateProgress(100)

    // Inline mode: add hidden field to parent form + show chip (no POST URL)
    if (!this.urlValue) {
      this.addInlineAttachment(blob)
      this.hideProgress()
      return
    }

    // Standard mode: POST to the URL
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = this.urlValue
    form.style.display = 'none'

    // Add CSRF token
    // The one consumer that needs the raw token rather than a header: it goes into a hidden
    // authenticity_token input on a real form submission.
    const token = csrfToken()
    if (token) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = token
      form.appendChild(csrfInput)
    }

    // Add signed_id
    const signedIdInput = document.createElement('input')
    signedIdInput.type = 'hidden'
    signedIdInput.name = 'signed_id'
    signedIdInput.value = blob.signed_id
    form.appendChild(signedIdInput)

    // Request turbo_stream response
    const acceptInput = document.createElement('input')
    acceptInput.type = 'hidden'
    acceptInput.name = '_turbo_stream'
    acceptInput.value = 'true'
    form.appendChild(acceptInput)

    document.body.appendChild(form)
    form.requestSubmit()
    form.remove()
  }

  addInlineAttachment(blob) {
    // Add hidden field to the closest form
    const parentForm = this.element.closest('form')
    if (!parentForm) return

    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'attachment_signed_ids[]'
    hiddenInput.value = blob.signed_id
    hiddenInput.dataset.blobFilename = blob.filename
    parentForm.appendChild(hiddenInput)

    // Add visual chip
    const chipsContainer = this.element.querySelector('#attachment-chips') || this.element
    const chip = document.createElement('div')
    chip.className = this.classesFor('chip').join(' ')
    chip.innerHTML = `
      <span class="${this.classesFor('chipLabel').join(' ')}">${blob.filename}</span>
      <button type="button" class="${this.classesFor('chipRemove').join(' ')}" data-action="click->direct-upload#removeInlineAttachment">
        &times;
      </button>
    `
    chip.dataset.signedId = blob.signed_id
    chipsContainer.appendChild(chip)
  }

  removeInlineAttachment(event) {
    const chip = event.currentTarget.closest('[data-signed-id]')
    if (!chip) return

    const signedId = chip.dataset.signedId
    const parentForm = this.element.closest('form')

    // Remove hidden field
    if (parentForm) {
      const hiddenInput = parentForm.querySelector(`input[name="attachment_signed_ids[]"][value="${signedId}"]`)
      if (hiddenInput) hiddenInput.remove()
    }

    // Remove chip
    chip.remove()
  }

  showError(message) {
    // Dispatch custom event for error display
    this.dispatch('error', { detail: { message } })
  }
}
